import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Loader2, RotateCcw, Archive, AlertCircle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ArchivedGoalsDialog({ open, onOpenChange, token, onRestored }) {
  const { t } = useTranslation();
  const [archivedWants, setArchivedWants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(null); // stores want_id being restored

  const fetchArchivedGoals = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/wants?include_archived=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter for archived ones
      const archived = data.filter((w) => w.archived === true);
      setArchivedWants(archived);
    } catch (error) {
      console.error('Failed to load archived goals:', error);
      toast.error('Failed to load archived goals');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (open) {
      fetchArchivedGoals();
    }
  }, [open, fetchArchivedGoals]);

  const handleRestore = async (want) => {
    setSubmitting(want.id);
    try {
      await axios.put(
        `${API}/wants/${want.id}`,
        {
          title: want.title,
          description: want.description,
          being_id: want.being_id,
          target_date: want.target_date,
          archived: false
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Restored "${want.title}"`);
      // Update local list
      await fetchArchivedGoals();
      // Notify parent to refresh main dashboard columns
      onRestored?.();
    } catch (error) {
      console.error('Failed to restore goal:', error);
      toast.error('Failed to restore goal');
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-x-hidden" data-testid="archived-goals-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl flex items-center gap-2">
            <Archive className="w-5 h-5 text-accent" />
            Archived Goals
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading archived goals...
          </div>
        ) : archivedWants.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-muted-foreground text-center">
            <AlertCircle className="w-8 h-8 mb-2 text-muted-foreground/60" />
            <p className="text-sm font-medium">No archived goals found.</p>
            <p className="text-xs text-muted-foreground/80 mt-1">Archived goals will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            <p className="text-xs text-muted-foreground mb-4">
              Here are goals you've archived. Restoring a goal will return it to your active board along with its tasks.
            </p>
            <div className="divide-y divide-border/40 max-h-[50vh] overflow-y-auto pr-1">
              {archivedWants.map((want) => (
                <div key={want.id} className="py-3 flex items-start justify-between gap-4 first:pt-0 last:pb-0">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm text-foreground truncate" title={want.title}>{want.title}</h4>
                    {want.description && (
                      <p className="text-xs text-muted-foreground break-words mt-0.5">{want.description}</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRestore(want)}
                    disabled={submitting !== null}
                    className="flex-shrink-0 h-8 text-xs rounded-full px-3"
                    data-testid={`restore-goal-${want.id}`}
                  >
                    {submitting === want.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <>
                        <RotateCcw className="w-3 h-3 mr-1" />
                        Restore
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
